/**
 * Photo gallery — single column at 375 px, three columns at desktop.
 * Mobile-first per constitution VII.
 */

import { PhotoCard } from "./PhotoCard";
import { PhotoUploadButton } from "./PhotoUploadButton";
import type { Photo } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  photos: Photo[];
  needsScale: boolean;
  readOnly?: boolean;
  onUploaded: (photo: Photo) => void;
  onDeleted: (photoId: string) => void;
}

export function PhotoGallery({
  estimateId,
  photos,
  needsScale,
  readOnly = false,
  onUploaded,
  onDeleted,
}: Props) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-serif text-2xl text-starz-navy">Project photos</h2>
        {!readOnly && (
          <PhotoUploadButton
            estimateId={estimateId}
            currentCount={photos.length}
            onUploaded={onUploaded}
          />
        )}
      </div>

      {photos.length === 0 ? (
        <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/50 p-8 text-center text-sm text-warm-gray">
          No photos yet. Stella will help you decide which photos to take — a wide shot of the room
          and one of the surfaces you care about are usually a good start.
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-3" aria-label="Project photo gallery">
          {photos.map((p) => (
            <li key={p.photo_id}>
              <PhotoCard
                photo={p}
                estimateId={estimateId}
                needsScale={needsScale}
                readOnly={readOnly}
                onDeleted={onDeleted}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
